import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  ImageList,
  ImageListItem,
} from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { Add, Delete, Edit, Warehouse, PhotoCamera, Close } from '@mui/icons-material';
import { locationService } from '../services/location.service';
import { moldService } from '../services/mold.service';
import storageService from '../services/storage.service';
import { Location } from '../types';

const buildSurface = (theme: Theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
  backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
    theme.palette.primary.dark,
    0.22,
  )})`,
  boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
  backdropFilter: 'blur(12px)',
});

const LocationManagement: React.FC = () => {
  const theme = useTheme<Theme>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Location, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    zone: '',
    capacity: 0,
    currentCount: 0,
    description: '',
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      
      // 보관처 및 금형 데이터 로드
      const [locationsData, moldsData] = await Promise.all([
        locationService.getAll(),
        moldService.getMolds()
      ]);
      
      // 각 보관처별 실제 금형 개수 계산
      const updatedLocations = locationsData.map(location => {
        const actualCount = moldsData.filter(
          mold => mold.location === location.name && mold.status === 'stored'
        ).length;
        
        return {
          ...location,
          currentCount: actualCount
        };
      });
      
      setLocations(updatedLocations);
      setError('');
    } catch (err) {
      setError('보관처 정보를 불러올 수 없습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setEditingId(location.id || null);
      setFormData({
        name: location.name,
        zone: location.zone,
        capacity: location.capacity,
        currentCount: location.currentCount,
        description: location.description || '',
      });
      setImagePreviews(location.thumbnails || location.images || []);
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        zone: '',
        capacity: 0,
        currentCount: 0,
        description: '',
      });
      setImagePreviews([]);
    }
    setImageFiles([]);
    setOpenDialog(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setImageFiles(prev => [...prev, ...files]);
      
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemovePreview = (index: number) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      let locationData = { ...formData };
      
      if (editingId) {
        // 이미지 업로드 (있는 경우)
        if (imageFiles.length > 0) {
          const imageUrls: string[] = [];
          const thumbnailUrls: string[] = [];
          
          for (let i = 0; i < imageFiles.length; i++) {
            const { original, thumbnail } = await storageService.uploadLocationImage(
              imageFiles[i],
              editingId,
              i
            );
            imageUrls.push(original);
            thumbnailUrls.push(thumbnail);
          }
          
          locationData = { 
            ...locationData, 
            images: [...(locationData.images || []), ...imageUrls],
            thumbnails: [...(locationData.thumbnails || []), ...thumbnailUrls]
          };
        }
        await locationService.update(editingId, locationData);
      } else {
        // 신규 등록
        const newId = await locationService.create(locationData);
        
        // 이미지 업로드 후 업데이트 (있는 경우)
        if (imageFiles.length > 0 && newId) {
          const imageUrls: string[] = [];
          const thumbnailUrls: string[] = [];
          
          for (let i = 0; i < imageFiles.length; i++) {
            const { original, thumbnail } = await storageService.uploadLocationImage(
              imageFiles[i],
              newId,
              i
            );
            imageUrls.push(original);
            thumbnailUrls.push(thumbnail);
          }
          
          await locationService.update(newId, { 
            images: imageUrls, 
            thumbnails: thumbnailUrls 
          });
        }
      }
      
      await loadLocations();
      setOpenDialog(false);
      setImageFiles([]);
      setImagePreviews([]);
    } catch (err) {
      setError('보관처 저장 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await locationService.delete(id);
      await loadLocations();
      setDeleteConfirm(null);
    } catch (err) {
      setError('보관처 삭제에 실패했습니다.');
      console.error(err);
    }
  };

  const getUsagePercent = (location: Location) => {
    if (location.capacity === 0) return 0;
    return (location.currentCount / location.capacity) * 100;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          위치 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            보관장소 관리
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
            보관 구역의 수용 가능량과 사용 현황을 한 눈에 확인하세요.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ px: 3.5, py: 1.1, fontWeight: 600, letterSpacing: '0.06em' }}
        >
          보관처 추가
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)'
          },
          gap: 2.5
        }}
      >
        {locations.map((location) => {
          const usagePercent = getUsagePercent(location);
          const isNearFull = usagePercent >= 80;

          return (
            <Card key={location.id} sx={{ ...buildSurface(theme), height: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                {/* 왼쪽: 정보 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Warehouse color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                      {location.name}
                    </Typography>
                    <Chip label={`구역: ${location.zone}`} size="small" color="primary" />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    보관량: {location.currentCount} / {location.capacity} ( {usagePercent.toFixed(0)}% )
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    color={isNearFull ? 'warning' : 'primary'}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: alpha(theme.palette.primary.light, 0.12),
                      mb: 1
                    }}
                  />
                  {location.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {location.description}
                    </Typography>
                  )}
                  
                  {/* 버튼 */}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <IconButton
                      onClick={() => handleOpenDialog(location)}
                      color="primary"
                      size="small"
                      sx={{
                        border: `1px solid ${alpha(theme.palette.primary.light, 0.4)}`,
                        borderRadius: 2,
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => setDeleteConfirm(location.id || null)}
                      color="error"
                      size="small"
                      sx={{
                        border: `1px solid ${alpha(theme.palette.error.light, 0.4)}`,
                        borderRadius: 2,
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </Box>

                {/* 오른쪽: 썸네일 */}
                {location.thumbnails && location.thumbnails.length > 0 && (
                  <Box
                    component="img"
                    src={location.thumbnails[0]}
                    alt={location.name}
                    sx={{
                      width: { xs: 100, md: 140 },
                      height: { xs: 100, md: 140 },
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: `2px solid ${alpha(theme.palette.primary.light, 0.3)}`,
                      flexShrink: 0
                    }}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? '보관처 수정' : '보관처 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 이미지 업로드 */}
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<PhotoCamera />}
                size="small"
                fullWidth
              >
                이미지 추가
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                />
              </Button>
              
              {imagePreviews.length > 0 && (
                <ImageList sx={{ mt: 2 }} cols={3} gap={8}>
                  {imagePreviews.map((preview, index) => (
                    <ImageListItem key={index} sx={{ position: 'relative' }}>
                      <img
                        src={preview}
                        alt={`preview-${index}`}
                        style={{ height: 100, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePreview(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'error.light' }
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Box>
            
            <TextField
              label="보관처명"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="예: A동-1존"
              required
              fullWidth
            />
            <TextField
              label="구역"
              value={formData.zone}
              onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
              placeholder="예: A, B, C"
              required
              fullWidth
            />
            <TextField
              label="최대 수용량"
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value, 10) || 0 })}
              required
              fullWidth
            />
            <TextField
              label="현재 보관 수량"
              type="number"
              value={formData.currentCount}
              onChange={(e) => setFormData({ ...formData, currentCount: parseInt(e.target.value, 10) || 0 })}
              required
              fullWidth
            />
            <TextField
              label="비고"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {editingId ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>보관처 삭제</DialogTitle>
        <DialogContent>
          <Typography>선택한 보관처를 삭제하시겠습니까?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            color="error"
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationManagement;
